import { dailyPuzzle, generatePuzzle } from '../core/generate';
import { computeStreak, isoToday } from '../core/dates';
import { categoryAccuracy, loadProfile, recordRound, saveProfile } from '../core/profile';
import { scoreRound } from '../core/score';
import { CATEGORIES, type Category, type Profile, type Puzzle, type RoundResult, type Tier } from '../core/types';

const CATEGORY_LABELS: Record<Category, string> = {
  logic: 'Logic',
  'null-safety': 'Null safety',
  security: 'Security',
  performance: 'Performance',
  style: 'Style',
};

const TIER_LABELS: Record<Tier, string> = {
  novice: 'Novice',
  intermediate: 'Intermediate',
  expert: 'Expert',
};

interface RoundContext {
  puzzle: Puzzle;
  daily: boolean;
  annotations: Set<number>;
  secondsLeft: number;
  timerId: number | null;
}

let root: HTMLElement;
let profile: Profile;
let round: RoundContext | null = null;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = '',
  text = '',
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function stopTimer(): void {
  if (round?.timerId != null) {
    window.clearInterval(round.timerId);
    round.timerId = null;
  }
}

/* ---------------- home ---------------- */

function renderHome(): void {
  stopTimer();
  round = null;
  root.replaceChildren();

  const shell = el('div', 'shell');
  const header = el('header', 'masthead');
  header.append(el('h1', '', 'Code Review Gauntlet'));
  header.append(
    el(
      'p',
      'tagline',
      'A reviewer walks into a diff. Find every planted bug, security hole, and code smell before the timer runs out.',
    ),
  );
  shell.append(header);

  const today = isoToday();
  const streak = computeStreak(profile.dailyDays, today);
  const dailyDone = today in profile.dailyScores;

  const modes = el('section', 'modes');

  const daily = el('article', 'mode-card daily');
  daily.append(el('h2', '', 'Daily challenge'));
  const preview = dailyPuzzle(today);
  daily.append(
    el('p', '', `Everyone gets the same seeded puzzle. Today: ${TIER_LABELS[preview.tier].toLowerCase()} tier.`),
  );
  const streakLine = el('p', 'streak', streak > 0 ? `🔥 ${streak}-day streak` : 'No streak yet — start one today.');
  daily.append(streakLine);
  const dailyBtn = el('button', 'primary');
  if (dailyDone) {
    dailyBtn.textContent = `Done today — ${profile.dailyScores[today]} pts. Replay (unscored)`;
    dailyBtn.classList.add('done');
  } else {
    dailyBtn.textContent = 'Play today’s puzzle';
  }
  dailyBtn.addEventListener('click', () => startRound(dailyPuzzle(today), true));
  daily.append(dailyBtn);
  modes.append(daily);

  const practice = el('article', 'mode-card');
  practice.append(el('h2', '', 'Practice'));
  practice.append(el('p', '', 'A fresh seeded round at the difficulty you pick.'));
  const tierRow = el('div', 'tier-row');
  (['novice', 'intermediate', 'expert'] as const).forEach((tier) => {
    const btn = el('button', `tier tier-${tier}`, TIER_LABELS[tier]);
    btn.addEventListener('click', () => {
      const seed = Math.floor(Math.random() * 2 ** 32);
      startRound(generatePuzzle(seed, tier), false);
    });
    tierRow.append(btn);
  });
  practice.append(tierRow);
  practice.append(el('p', 'fine', describeLibrary()));
  modes.append(practice);

  shell.append(modes);
  shell.append(renderProfilePanel());
  shell.append(renderFooter());
  root.append(shell);
}

function describeLibrary(): string {
  return 'Puzzles are generated by a seeded mutation engine: deterministic, offline, never the same round twice.';
}

function renderProfilePanel(): HTMLElement {
  const panel = el('section', 'profile');
  panel.append(el('h2', '', 'Your reviewer profile'));

  const grid = el('div', 'profile-grid');

  const stats = el('div', 'stats');
  const addStat = (label: string, value: string): void => {
    const box = el('div', 'stat');
    box.append(el('div', 'stat-value', value));
    box.append(el('div', 'stat-label', label));
    stats.append(box);
  };
  addStat('rounds played', String(profile.gamesPlayed));
  addStat('best score', String(profile.bestScore));
  addStat(
    'avg score',
    profile.gamesPlayed > 0 ? String(Math.round(profile.totalScore / profile.gamesPlayed)) : '—',
  );
  addStat('daily streak', String(computeStreak(profile.dailyDays, isoToday())));
  grid.append(stats);

  grid.append(renderRadar());
  panel.append(grid);

  if (profile.gamesPlayed === 0) {
    panel.append(el('p', 'fine', 'Play a few rounds and the radar shows which bug categories you miss most.'));
  }
  return panel;
}

/** Pentagon radar of per-category accuracy, hand-rolled SVG. */
function renderRadar(): HTMLElement {
  const wrap = el('div', 'radar');
  const size = 280;
  const cx = size / 2;
  const cy = size / 2 + 6;
  const radius = 82;
  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', 'Accuracy by defect category');

  const point = (index: number, r: number): [number, number] => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / CATEGORIES.length;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };

  for (const ring of [0.33, 0.66, 1]) {
    const poly = document.createElementNS(svgNs, 'polygon');
    poly.setAttribute(
      'points',
      CATEGORIES.map((_, i) => point(i, radius * ring).join(',')).join(' '),
    );
    poly.setAttribute('class', 'radar-ring');
    svg.append(poly);
  }

  const values = CATEGORIES.map((c) => categoryAccuracy(profile, c) ?? 0);
  const data = document.createElementNS(svgNs, 'polygon');
  data.setAttribute(
    'points',
    CATEGORIES.map((_, i) => point(i, Math.max(4, radius * values[i])).join(',')).join(' '),
  );
  data.setAttribute('class', 'radar-data');
  svg.append(data);

  CATEGORIES.forEach((c, i) => {
    const [x, y] = point(i, radius + 18);
    const label = document.createElementNS(svgNs, 'text');
    label.setAttribute('x', String(x));
    label.setAttribute('y', String(y));
    label.setAttribute('class', 'radar-label');
    label.setAttribute('text-anchor', 'middle');
    const acc = categoryAccuracy(profile, c);
    label.textContent = acc === null ? CATEGORY_LABELS[c] : `${CATEGORY_LABELS[c]} ${Math.round(acc * 100)}%`;
    svg.append(label);
  });

  wrap.append(svg);
  return wrap;
}

function renderFooter(): HTMLElement {
  const footer = el('footer', 'footer');
  const p = el('p');
  p.append('Fully client-side: seeded deterministic puzzles, progress in localStorage. ');
  const a = el('a', '', 'Source on GitHub');
  a.href = 'https://github.com/pisanuw/Claude-capstone/tree/main/code-review-gauntlet';
  a.rel = 'noopener noreferrer';
  a.target = '_blank';
  p.append(a);
  footer.append(p);
  return footer;
}

/* ---------------- round ---------------- */

function startRound(puzzle: Puzzle, daily: boolean): void {
  stopTimer();
  round = { puzzle, daily, annotations: new Set(), secondsLeft: puzzle.timeLimit, timerId: null };
  renderRound();
  round.timerId = window.setInterval(() => {
    if (!round) return;
    round.secondsLeft -= 1;
    updateTimer();
    if (round.secondsLeft <= 0) submitRound();
  }, 1000);
}

function renderRound(): void {
  if (!round) return;
  const { puzzle } = round;
  root.replaceChildren();

  const shell = el('div', 'shell');
  const bar = el('div', 'round-bar');
  const meta = el('div', 'round-meta');
  meta.append(el('span', 'chip lang', puzzle.language));
  meta.append(el('span', `chip tier-chip tier-${puzzle.tier}`, TIER_LABELS[puzzle.tier]));
  meta.append(el('span', 'chip', round.daily ? 'daily challenge' : `seed ${puzzle.seed}`));
  bar.append(meta);

  const timerWrap = el('div', 'timer');
  timerWrap.append(el('div', 'timer-fill'));
  timerWrap.append(el('span', 'timer-text'));
  bar.append(timerWrap);
  shell.append(bar);

  shell.append(el('h2', 'round-title', puzzle.title));
  shell.append(
    el(
      'p',
      'round-brief',
      `${puzzle.defects.length === 1 ? 'One defect is' : `${puzzle.defects.length} defects are`} planted in this snippet. Click every line you would flag in review, then submit.`,
    ),
  );

  const codePanel = el('div', 'code-panel');
  const pre = el('pre', 'code');
  puzzle.code.forEach((text, i) => {
    const line = el('div', 'code-line');
    line.dataset.line = String(i + 1);
    line.append(el('span', 'line-no', String(i + 1)));
    line.append(el('span', 'line-text', text.length > 0 ? text : ' '));
    line.addEventListener('click', () => toggleLine(i + 1, line));
    pre.append(line);
  });
  codePanel.append(pre);
  shell.append(codePanel);

  const actions = el('div', 'round-actions');
  const count = el('span', 'flag-count');
  actions.append(count);
  const submit = el('button', 'primary', 'Submit review');
  submit.addEventListener('click', () => submitRound());
  actions.append(submit);
  const quit = el('button', 'ghost', 'Abandon');
  quit.addEventListener('click', () => renderHome());
  actions.append(quit);
  shell.append(actions);

  root.append(shell);
  updateFlagCount();
  updateTimer();
}

function toggleLine(line: number, node: HTMLElement): void {
  if (!round) return;
  if (round.annotations.has(line)) {
    round.annotations.delete(line);
    node.classList.remove('flagged');
  } else {
    round.annotations.add(line);
    node.classList.add('flagged');
  }
  updateFlagCount();
}

function updateFlagCount(): void {
  const count = root.querySelector('.flag-count');
  if (count && round) {
    const n = round.annotations.size;
    count.textContent = n === 1 ? '1 line flagged' : `${n} lines flagged`;
  }
}

function updateTimer(): void {
  if (!round) return;
  const fill = root.querySelector<HTMLElement>('.timer-fill');
  const text = root.querySelector('.timer-text');
  const ratio = Math.max(0, round.secondsLeft / round.puzzle.timeLimit);
  if (fill) {
    fill.style.width = `${ratio * 100}%`;
    fill.classList.toggle('low', ratio < 0.25);
  }
  if (text) text.textContent = `${Math.max(0, round.secondsLeft)}s`;
}

/* ---------------- result ---------------- */

function submitRound(): void {
  if (!round) return;
  stopTimer();
  const { puzzle, daily, annotations, secondsLeft } = round;
  const result = scoreRound(puzzle, [...annotations], Math.max(0, secondsLeft));
  const today = isoToday();
  profile = recordRound(profile, puzzle, result, daily ? today : null);
  saveProfile(storage(), profile);
  renderResult(result);
}

function renderResult(result: RoundResult): void {
  if (!round) return;
  const { puzzle, daily, annotations } = round;
  root.replaceChildren();

  const shell = el('div', 'shell');
  const summary = el('section', `result-summary grade-${result.grade}`);
  summary.append(el('div', 'grade', result.grade));
  const details = el('div', 'result-details');
  details.append(el('h2', '', `${result.score} points`));
  const parts: string[] = [
    `${result.found.length}/${puzzle.defects.length} defects found`,
    `${result.falsePositives.length} false ${result.falsePositives.length === 1 ? 'flag' : 'flags'}`,
  ];
  if (result.timeBonus > 0) parts.push(`+${result.timeBonus} time bonus`);
  details.append(el('p', '', parts.join(' · ')));
  if (daily) {
    const streak = computeStreak(profile.dailyDays, isoToday());
    details.append(el('p', 'streak', streak > 0 ? `🔥 ${streak}-day streak` : ''));
  }
  summary.append(details);
  shell.append(summary);

  const codePanel = el('div', 'code-panel');
  const pre = el('pre', 'code');
  const defectLines = new Map(puzzle.defects.map((d) => [d.line, d]));
  puzzle.code.forEach((text, i) => {
    const lineNo = i + 1;
    const line = el('div', 'code-line');
    const defect = defectLines.get(lineNo);
    if (defect) line.classList.add(result.found.includes(defect) ? 'was-found' : 'was-missed');
    else if (annotations.has(lineNo)) line.classList.add('was-false');
    line.append(el('span', 'line-no', String(lineNo)));
    line.append(el('span', 'line-text', text.length > 0 ? text : ' '));
    pre.append(line);
  });
  codePanel.append(pre);
  shell.append(codePanel);

  const explainer = el('section', 'explainer');
  explainer.append(el('h2', '', 'The defects'));
  for (const defect of puzzle.defects) {
    const found = result.found.includes(defect);
    const card = el('article', `defect ${found ? 'found' : 'missed'}`);
    const head = el('div', 'defect-head');
    head.append(el('span', 'defect-status', found ? '✓ found' : '✗ missed'));
    head.append(el('span', `chip cat-${defect.category}`, CATEGORY_LABELS[defect.category]));
    head.append(el('span', 'chip', `line ${defect.line}`));
    head.append(el('span', `chip tier-chip tier-${defect.tier}`, TIER_LABELS[defect.tier]));
    card.append(head);
    card.append(el('h3', '', defect.title));
    card.append(el('p', '', defect.explanation));
    const fix = el('p', 'fix');
    fix.append(el('strong', '', 'Fix: '));
    fix.append(defect.fix);
    card.append(fix);
    explainer.append(card);
  }
  if (result.falsePositives.length > 0) {
    explainer.append(
      el(
        'p',
        'fine',
        `False flags on line${result.falsePositives.length === 1 ? '' : 's'} ${result.falsePositives.join(', ')}: those lines are clean (−25 points each).`,
      ),
    );
  }
  shell.append(explainer);

  const actions = el('div', 'round-actions');
  if (!daily) {
    const again = el('button', 'primary', 'New round, same tier');
    again.addEventListener('click', () => {
      const seed = Math.floor(Math.random() * 2 ** 32);
      startRound(generatePuzzle(seed, puzzle.tier), false);
    });
    actions.append(again);
  }
  const home = el('button', daily ? 'primary' : 'ghost', 'Back to menu');
  home.addEventListener('click', () => renderHome());
  actions.append(home);
  shell.append(actions);

  root.append(shell);
}

/* ---------------- mount ---------------- */

export function mountApp(container: HTMLElement): void {
  root = container;
  profile = loadProfile(storage());
  renderHome();
}
